import { getGlobalConfig } from '../config';
import { CONSENT } from '@sklik/cmp2-common';
import { DOT_CONFIG_PROPERTIES, IDENTITIES_KEYS, IDENTITY_STATE, } from '../../types';
import { TIMEOUT } from '../consts';
import { createIdentityObject } from '../identities';
import { isObject } from '../utils';
/**
 * Get a shallow copy of `window.login.current` and `window.login.current.others`
 * if at least one parameter in `keys` is truthy
 */
export const getLoginObject = () => {
    var _a, _b, _c;
    const keys = ['advert_uid', 'uid'];
    const hasLoginId = keys.some((key) => { var _a, _b; return (_b = (_a = window.login) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b[key]; });
    const isLoginForgotten = ((_b = (_a = window.login) === null || _a === void 0 ? void 0 : _a.current) === null || _b === void 0 ? void 0 : _b.state) === 'forget';
    if (!((_c = window.login) === null || _c === void 0 ? void 0 : _c.current) || (!hasLoginId && !isLoginForgotten)) {
        return undefined;
    }
    const loginCopy = Object.assign({}, window.login.current);
    if (window.login.current.others) {
        loginCopy.others = [...window.login.current.others];
    }
    return loginCopy;
};
export const getBadgeFromDot = () => { var _a; return !!((_a = window.DOT) === null || _a === void 0 ? void 0 : _a.getCfgValue(DOT_CONFIG_PROPERTIES.badge)); };
/**
 * Get consent value from window.login object (copy "advert_uid" to "said")
 */
export const getLoginFromWindowObject = () => {
    const output = getLoginObject();
    if (!output) {
        return;
    }
    if (output.advert_uid) {
        output.said = output.advert_uid;
    }
    if (Array.isArray(output.others)) {
        output.others = output.others.map((o) => {
            return isObject(o) && o.advert_uid ? Object.assign(Object.assign({}, o), { said: o.advert_uid }) : o;
        });
    }
    return output;
};
/**
 * Add login to identitites object (if any)
 */
export const getLogin = (identities) => {
    const login = getLoginFromWindowObject();
    if (login) {
        identities[IDENTITIES_KEYS.login] = createIdentityObject(identities[IDENTITIES_KEYS.login], login, IDENTITY_STATE.fresh, true);
    }
};
/**
 * Listen to events emited by BADGE
 */
export const listenToBadge = (updater, getter) => {
    var _a, _b;
    const handler = () => {
        var _a;
        const existsInStorage = getter(IDENTITIES_KEYS.login);
        const login = getLoginFromWindowObject();
        if (login || existsInStorage) {
            const value = (login || existsInStorage.value);
            const state = login ? IDENTITY_STATE.fresh : existsInStorage.state;
            const updated = { login: { value, state } };
            const newUid = login === null || login === void 0 ? void 0 : login.advert_uid;
            const oldUid = (_a = existsInStorage === null || existsInStorage === void 0 ? void 0 : existsInStorage.value) === null || _a === void 0 ? void 0 : _a.advert_uid;
            // "rusId" changed? let wipe out consent and wait for fresh value
            if ((newUid || oldUid) && newUid !== oldUid) {
                updated.consent = { value: null, state: IDENTITY_STATE.fresh };
                if (newUid) {
                    updated.id5 = { value: null, state: IDENTITY_STATE.fresh };
                }
            }
            // hack for websites without CMP dialog
            if (hasLoginConsent(login)) {
                const consentResultLogin = getLoginConsentResultFromCookie('v2', getLoginConsent(login).v2.encodedCookie);
                updated.consent = {
                    value: consentResultLogin,
                    state: IDENTITY_STATE.fresh,
                };
            }
            updater(updated);
        }
    };
    const loginTimer = window.setTimeout(() => {
        handler();
    }, ((_b = (_a = getGlobalConfig()) === null || _a === void 0 ? void 0 : _a.timeouts) === null || _b === void 0 ? void 0 : _b[IDENTITIES_KEYS.login]) || TIMEOUT);
    const done = () => {
        window.clearTimeout(loginTimer);
        handler();
    };
    window.addEventListener('badge', done);
    window.addEventListener('login', done);
    window.addEventListener('logout', done);
    window.addEventListener('forget', done);
};
/**
 * Get parsed euconsent object from login identity
 */
export const getLoginConsent = (loginIdentity) => {
    let loginConsent = null;
    if (loginIdentity) {
        try {
            loginConsent = JSON.parse(loginIdentity.euconsent);
        }
        catch (_a) {
            // no-op
        }
    }
    return loginConsent;
};
/**
 * Checks if login identity contains consent string
 */
export const hasLoginConsent = (loginIdentity) => {
    var _a, _b;
    return !!((_b = (_a = getLoginConsent(loginIdentity)) === null || _a === void 0 ? void 0 : _a.v2) === null || _b === void 0 ? void 0 : _b.encodedCookie);
};
/**
 * Gets ConsentResult object from login cookie string
 * @param consentString consent encoded string from login
 */
export const getLoginConsentResultFromCookie = (version, consentString) => {
    if (!consentString) {
        return null;
    }
    return {
        version,
        name: `euconsent-${version}`,
        consentString,
        purposeOne: CONSENT.hasPurposeOne(consentString),
    };
};
